const LOCATION_CACHE_KEY = "xpanse_current_location";
const LOCATION_CACHE_TIME = 1000 * 60 * 45;

export function getCachedLocation() {
    try {
        const cached = localStorage.getItem(LOCATION_CACHE_KEY);

        if (!cached) return null;

        const parsed = JSON.parse(cached);
        const isFresh =
            parsed?.timestamp &&
            Date.now() - parsed.timestamp < LOCATION_CACHE_TIME;

        if (
            !isFresh ||
            typeof parsed.latitude !== "number" ||
            typeof parsed.longitude !== "number"
        ) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

function saveLocationToCache(location) {
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(location));
}

async function reverseGeocode(latitude, longitude) {
    const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
    );

    if (!response.ok) {
        throw new Error("Address lookup failed");
    }

    const data = await response.json();
    const address = data.address || {};

    const street =
        address.road ||
        address.pedestrian ||
        address.neighbourhood ||
        address.suburb ||
        "";

    const city =
        address.city ||
        address.town ||
        address.village ||
        address.county ||
        "";

    const state = address.state || "";

    return (
        [street, city, state].filter(Boolean).join(", ") ||
        data.display_name ||
        ""
    );
}

export function captureCurrentLocation({ maximumAge = 0 } = {}) {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("This device does not support location."));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;
                const accuracy = position.coords.accuracy;
                let name = "";

                try {
                    name = await reverseGeocode(latitude, longitude);
                } catch {
                    name = "";
                }

                if (!name) {
                    name = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
                }

                const location = {
                    latitude,
                    longitude,
                    accuracy,
                    name,
                    timestamp: Date.now(),
                };

                saveLocationToCache(location);
                resolve(location);
            },
            (error) => {
                if (error?.code === 1) {
                    reject(
                        new Error(
                            "Location permission is required to save a checklist."
                        )
                    );
                    return;
                }

                if (error?.code === 3) {
                    reject(
                        new Error("Location request timed out. Please try again.")
                    );
                    return;
                }

                reject(new Error("Unable to read your current location."));
            },
            {
                enableHighAccuracy: true,
                timeout: 12000,
                maximumAge,
            }
        );
    });
}
